import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { useCompany } from "@/context/CompanyContext";
import UsageBanner from "@/components/UsageBanner";
import { IbanApprovalBanner } from "@/components/IbanApprovalBanner";
import { ReverseChargeThresholdCard } from "@/components/ReverseChargeThresholdCard";
import { SimpleModeThresholdBanner } from "@/components/SimpleModeBanner";
import { DpaReminderBanner } from "@/components/DpaReminderBanner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  FileText,
  FileInput,
  AlertTriangle,
  Inbox,
  ArrowRight,
  Landmark,
  Bell,
  Sparkles,
  Wallet,
  Receipt,
  PlayCircle,
  Upload,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Sparkline } from "@/components/Sparkline";
import { MultiAssetTimelineWidget } from "@/components/MultiAssetTimelineWidget";
import { CashflowBridgeCard } from "@/components/CashflowBridgeCard";
import { AiTrustCard } from "@/components/AiTrustCard";
import { AssetWarningsWidget } from "@/components/AssetWarningsWidget";
import { CashflowForecastWidget } from "@/components/CashflowForecastWidget";
import { ActivityFeedWidget } from "@/components/ActivityFeedWidget";
import { AutoBookedTodayCard } from "@/components/AutoBookedTodayCard";
import { AnomaliesWidget } from "@/components/AnomaliesWidget";
import BankCockpitCard from "@/components/dashboard/BankCockpitCard";
import ClearingStatusCard from "@/components/dashboard/ClearingStatusCard";
import VatDeadlineCard from "@/components/dashboard/VatDeadlineCard";
import BankMatchSuggestionsCard from "@/components/dashboard/BankMatchSuggestionsCard";
import { ReceiptRadarWidget } from "@/components/dashboard/ReceiptRadarWidget";
import PrivateBudgetDashboard from "@/components/dashboard/PrivateBudgetDashboard";
import BudgetCockpitCard from "@/components/dashboard/BudgetCockpitCard";

const MONTH_LABELS = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

function formatMonth(key) {
  const [y, m] = key.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_LABELS[idx] || m} ${y.slice(2)}`;
}

function formatCHF(value, currency = "CHF") {
  const num = typeof value === "number" ? value : parseFloat(value || 0);
  return `${currency} ${num.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function TrendBadge({ current, prev, label, metric = "", invertColors = false }) {
  if (!Number.isFinite(current) || !Number.isFinite(prev)) return null;
  const diff = current - prev;
  const zeros = prev === 0 && current === 0;
  const pct = prev === 0 ? null : (diff / Math.abs(prev)) * 100;
  const up = diff > 0;
  const neutral = diff === 0;
  const goodWhenUp = !invertColors;
  const isGood = neutral ? true : up === goodWhenUp;
  const color = neutral || zeros
    ? "text-slate-500"
    : isGood
    ? "text-emerald-700"
    : "text-rose-600";
  const arrow = zeros || neutral ? "→" : up ? "↑" : "↓";
  const testid = metric ? `trend-${metric}-${label}` : `trend-${label}`;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${color}`}
      data-testid={testid}
      title={`Vormonat: ${prev.toLocaleString("de-CH", { minimumFractionDigits: 2 })}`}
    >
      <span>{arrow}</span>
      {zeros ? (
        <span>keine Daten {label}</span>
      ) : pct !== null ? (
        <span>{Math.abs(pct).toFixed(0)}% {label}</span>
      ) : (
        <span>neu</span>
      )}
    </span>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, tone = "neutral", testid, onClick, trend, sparkline, sparklineColor }) {
  const tones = {
    neutral: "bg-white border-slate-200 text-slate-900",
    dark: "bg-slate-900 border-slate-900 text-slate-50",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  const iconBg = {
    neutral: "bg-slate-100 text-slate-600",
    dark: "bg-slate-800 text-slate-200",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return (
    <Card
      className={`p-4 border ${tones[tone]} ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium uppercase tracking-wider opacity-70">
            {title}
          </div>
          <div className="mt-2 text-2xl font-semibold truncate">{value}</div>
          {subtitle && (
            <div className="mt-1 text-xs opacity-70 truncate">{subtitle}</div>
          )}
          {trend && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
              {trend}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {Icon && (
            <div className={`h-9 w-9 rounded-lg ${iconBg[tone]} flex items-center justify-center`}>
              <Icon className="h-4 w-4" />
            </div>
          )}
          {sparkline && sparkline.length >= 2 && (
            <Sparkline
              data={sparkline}
              width={84}
              height={26}
              stroke={sparklineColor || "currentColor"}
              fill={sparklineColor || "currentColor"}
              strokeWidth={1.75}
              data-testid={testid ? `${testid}-sparkline` : undefined}
              className="opacity-80"
            />
          )}
        </div>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { company, loading: companyLoading, hasNoCompany, loadFailed, retryLoad } = useCompany();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoMatchToday, setAutoMatchToday] = useState(null); // {applied, skipped, failed} | null
  const [vendorHygiene, setVendorHygiene] = useState(null);

  // Iter67: Onboarding-Wizard-Redirect — wenn neuer User noch keine echte Firma hat
  useEffect(() => {
    apiClient
      .get("/onboarding/status")
      .then((res) => {
        if (res.data?.needs_onboarding) {
          navigate("/onboarding", { replace: true });
        }
      })
      .catch(() => { /* fail-soft */ });
  }, [navigate]);

  useEffect(() => {
    if (!company?._id) return;
    apiClient
      .get(`/companies/${company._id}/dashboard/vendor-hygiene`)
      .then((r) => setVendorHygiene(r.data))
      .catch(() => setVendorHygiene(null));
  }, [company]);

  useEffect(() => {
    apiClient
      .get("/scheduler/bank-match/recent-runs?limit=20")
      .then((res) => {
        const runs = res.data || [];
        const todayISO = new Date().toISOString().slice(0, 10);
        const todaysRuns = runs.filter((r) => (r.started_at || "").slice(0, 10) === todayISO);
        const totals = todaysRuns.reduce(
          (acc, r) => ({
            applied: acc.applied + (r.applied_count || 0),
            skipped: acc.skipped + (r.skipped_low_score || 0),
            failed: acc.failed + (r.failed_count || 0),
            runs: acc.runs + 1,
          }),
          { applied: 0, skipped: 0, failed: 0, runs: 0 }
        );
        setAutoMatchToday(totals);
      })
      .catch((e) => console.error(e));
  }, []);

  const loadStats = useCallback(async (silent = false) => {
    const companyId = company?._id || localStorage.getItem("demoCompanyId");
    if (!companyId) {
      setStats(null);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const res = await apiClient.get(
        `/companies/${companyId}/dashboard-stats?months=6`
      );
      setStats(res.data);
      if (silent) toast.success("Dashboard aktualisiert");
    } catch (e) {
      console.error(e);
      if (silent) toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (company?._id) loadStats(false);
  }, [loadStats, company]);

  // Still loading company info
  if (companyLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-company-loading">
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Firma wird geladen …
        </div>
      </div>
    );
  }

  // Iter288: Firmen-Laden fehlgeschlagen (Cold-Start/Netzwerk) → Retry statt fälschlichem Onboarding
  if (loadFailed) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-load-failed">
        <Card className="max-w-md w-full p-8 text-center border-dashed">
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <RefreshCw className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Verbindung wird aufgebaut …</h2>
          <p className="mt-2 text-sm text-slate-500">
            Ihre Firma konnte gerade nicht geladen werden. Einen Moment warten und erneut versuchen.
          </p>
          <Button
            className="mt-5 rounded-full"
            onClick={retryLoad}
            data-testid="dashboard-retry-load-button"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Erneut versuchen
          </Button>
        </Card>
      </div>
    );
  }

  // Empty state (no company) — nur wenn die Firmenliste erfolgreich als leer bestätigt wurde
  if (hasNoCompany) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-empty-no-company">
        <Card className="max-w-md w-full p-8 text-center border-dashed">
          <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Inbox className="h-6 w-6 text-slate-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">
            Willkommen in Ihrer Buchhaltung
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Um zu starten, legen Sie zuerst Ihre Firma in den Einstellungen an.
          </p>
          <Button
            className="mt-5 rounded-full"
            onClick={() => navigate("/new-company")}
            data-testid="dashboard-create-company-button"
          >
            Firma anlegen
          </Button>
        </Card>
      </div>
    );
  }

  if (company?.company_type === "privat") {
    return <PrivateBudgetDashboard company={company} />;
  }

  const chartData = (stats?.monthly_series || []).map((p) => ({
    month: formatMonth(p.month),
    Einnahmen: Math.round(p.income * 100) / 100,
    Ausgaben: Math.round(p.expenses * 100) / 100,
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Übersicht
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Ihre wichtigsten Kennzahlen auf einen Blick
            {stats?.vat_period_label ? ` · ${stats.vat_period_label}` : ""}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadStats(true)}
          disabled={loading}
          data-testid="dashboard-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Iter94: Usage-Banner (erscheint bei ≥80% Quota oder Overrun) */}
      <UsageBanner companyId={company?._id || localStorage.getItem("demoCompanyId")} />
      <DpaReminderBanner companyId={company?._id || localStorage.getItem("demoCompanyId")} />
      <ReverseChargeThresholdCard companyId={company?._id} />
      <SimpleModeThresholdBanner />
      <IbanApprovalBanner companyId={company?._id || localStorage.getItem("demoCompanyId")} compact />

      {/* Iter68: Hero-Strip mit den 3 wichtigsten KPIs */}
      <div className="grid gap-4 md:grid-cols-3" data-testid="dashboard-hero-strip">
        {/* Liquidität */}
        <Card
          className="p-5 border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate("/bank-reconciliation")}
          data-testid="dashboard-hero-liquidity"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">
                <Wallet className="h-3.5 w-3.5" />
                Liquidität
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 truncate" data-testid="dashboard-hero-liquidity-value">
                {formatCHF(stats?.liquidity_total || 0, stats?.currency)}
              </div>
              {stats?.liquidity_accounts && stats.liquidity_accounts.length > 0 ? (
                <ul className="mt-3 space-y-0.5 text-xs text-slate-600" data-testid="dashboard-hero-liquidity-breakdown">
                  {stats.liquidity_accounts.slice(0, 3).map((a) => (
                    <li key={a.code} className="flex justify-between gap-2">
                      <span className="truncate">
                        <span className="font-mono text-slate-400 mr-1">{a.code}</span>
                        {a.name}
                      </span>
                      <span className="font-medium text-slate-700 flex-shrink-0">
                        {formatCHF(a.balance, stats?.currency)}
                      </span>
                    </li>
                  ))}
                  {stats.liquidity_accounts.length > 3 && (
                    <li className="text-[10px] text-slate-400">
                      + {stats.liquidity_accounts.length - 3} weitere Konten
                    </li>
                  )}
                </ul>
              ) : (
                <div className="mt-3 text-xs text-emerald-700/70" data-testid="dashboard-hero-liquidity-empty">
                  Kein Liquiditäts-Konto (10xx) gebucht
                </div>
              )}
            </div>
            <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Wallet className="h-5 w-5" />
            </div>
          </div>
        </Card>

        {/* Offene Forderungen */}
        <Card
          className="p-5 border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/40 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate("/invoices?status=open")}
          data-testid="dashboard-hero-receivables"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-700">
                <Receipt className="h-3.5 w-3.5" />
                Offene Forderungen
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 truncate" data-testid="dashboard-hero-receivables-value">
                {formatCHF(stats?.open_invoices_amount || 0, stats?.currency)}
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                <span data-testid="dashboard-hero-receivables-count">
                  {stats?.open_invoices_count || 0} Rechnung{stats?.open_invoices_count === 1 ? "" : "en"}
                </span>
                {stats?.overdue_invoices_count > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-rose-700 font-medium" data-testid="dashboard-hero-receivables-overdue">
                    <AlertTriangle className="h-3 w-3" />
                    {stats.overdue_invoices_count} überfällig
                  </span>
                )}
              </div>
            </div>
            <div className="h-10 w-10 rounded-xl bg-sky-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Receipt className="h-5 w-5" />
            </div>
          </div>
        </Card>

        {/* MWST-Zahllast */}
        <Card
          className="p-5 border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate("/reports?tab=vat")}
          data-testid="dashboard-hero-vat"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-700">
                <Landmark className="h-3.5 w-3.5" />
                MWST-Zahllast {stats?.vat_period_label || ""}
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 truncate" data-testid="dashboard-hero-vat-value">
                {formatCHF(stats?.vat_current_period_liability || 0, stats?.currency)}
              </div>
              <div className="mt-3 text-xs text-slate-600">
                {(stats?.vat_current_period_liability || 0) > 0
                  ? "Schätzung — fällig an die ESTV"
                  : "Aktuelle Periode noch ausgeglichen"}
              </div>
            </div>
            <div className="h-10 w-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Landmark className="h-5 w-5" />
            </div>
          </div>
        </Card>
      </div>

      {/* Iter83: Bank-Cockpit-Card — Live-Saldi pro Bankkonto + Sync-Status + Unmatched-Counts */}
      <VatDeadlineCard companyId={company?._id} />
      <BudgetCockpitCard companyId={company?._id} />
      <BankCockpitCard companyId={company?._id} />
      <ClearingStatusCard companyId={company?._id} />
      <BankMatchSuggestionsCard />
      <ReceiptRadarWidget companyId={company?._id} />

      {/* Iter68: Empty-State-Banner — sichtbar nur wenn die Firma noch komplett leer ist */}
      {stats?.is_empty && (
        <Card
          className="p-6 border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-white"
          data-testid="dashboard-empty-state-banner"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
              <PlayCircle className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <h3 className="text-base font-semibold text-slate-900">
                Willkommen — leg los in 3 Schritten
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Hier laufen gleich alle Zahlen ein. Starte mit dem, was sich am schnellsten lohnt:
              </p>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => navigate("/receipts")}
                  data-testid="dashboard-empty-cta-receipt"
                >
                  <Upload className="h-4 w-4" />
                  Beleg hochladen
                </Button>
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => navigate("/invoices/new")}
                  data-testid="dashboard-empty-cta-invoice"
                >
                  <FileText className="h-4 w-4" />
                  Rechnung erstellen
                </Button>
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => navigate("/bank-reconciliation")}
                  data-testid="dashboard-empty-cta-bank"
                >
                  <Building2 className="h-4 w-4" />
                  Bank importieren
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* KPI Cards — sekundäre Ebene mit Trends */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Einnahmen"
          value={formatCHF(stats?.income_total || 0, stats?.currency)}
          subtitle="Ertrag laufendes Jahr (alle Buchungen)"
          icon={TrendingUp}
          tone="emerald"
          testid="dashboard-total-income-card"
          sparkline={(stats?.monthly_series || []).map((p) => p.income)}
          sparklineColor="#059669"
          trend={
            <>
              <TrendBadge
                current={stats?.income_current_month || 0}
                prev={stats?.income_prev_month || 0}
                label="vs. Vormonat"
                metric="income"
              />
              <TrendBadge
                current={stats?.income_current_month || 0}
                prev={stats?.income_prev_year_month || 0}
                label="vs. Vorjahr"
                metric="income"
              />
            </>
          }
        />
        <StatCard
          title="Ausgaben"
          value={formatCHF(stats?.expenses_total || 0, stats?.currency)}
          subtitle="Aufwand laufendes Jahr (alle Buchungen)"
          icon={TrendingDown}
          tone="rose"
          testid="dashboard-total-expenses-card"
          sparkline={(stats?.monthly_series || []).map((p) => p.expenses)}
          sparklineColor="#e11d48"
          trend={
            <>
              <TrendBadge
                current={stats?.expenses_current_month || 0}
                prev={stats?.expenses_prev_month || 0}
                label="vs. Vormonat"
                metric="expenses"
                invertColors
              />
              <TrendBadge
                current={stats?.expenses_current_month || 0}
                prev={stats?.expenses_prev_year_month || 0}
                label="vs. Vorjahr"
                metric="expenses"
                invertColors
              />
            </>
          }
        />
        <StatCard
          title="Gewinn"
          value={formatCHF(stats?.profit || 0, stats?.currency)}
          subtitle={stats?.profit >= 0 ? "Positives Ergebnis" : "Verlust"}
          icon={TrendingUp}
          tone="dark"
          testid="dashboard-profit-card"
          sparkline={(stats?.monthly_series || []).map((p) => (p.income || 0) - (p.expenses || 0))}
          sparklineColor="#94a3b8"
          trend={
            <>
              <TrendBadge
                current={stats?.profit_current_month || 0}
                prev={stats?.profit_prev_month || 0}
                label="vs. Vormonat"
                metric="profit"
              />
              <TrendBadge
                current={stats?.profit_current_month || 0}
                prev={stats?.profit_prev_year_month || 0}
                label="vs. Vorjahr"
                metric="profit"
              />
            </>
          }
        />
      </div>

      {/* Iter245: Cashflow-Brücke — vom Gewinn zum Kontostand */}
      <CashflowBridgeCard companyId={company?._id} />

      {/* Iter247: KI-Autopilot Vertrauens-Score */}
      <AiTrustCard companyId={company?._id} />

      {/* Auto-Match Heute-Card (nur wenn etwas passiert ist) */}
      {autoMatchToday && autoMatchToday.runs > 0 && (
        <Card
          className="p-4 border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white shadow-sm"
          data-testid="dashboard-auto-match-today-card"
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs font-medium text-emerald-800 uppercase tracking-wider">
                Auto Bank-Match heute
              </div>
              <div className="mt-1 flex items-center gap-4 flex-wrap">
                <span className="text-2xl font-semibold text-slate-900">
                  {autoMatchToday.applied}
                </span>
                <span className="text-xs text-slate-600">
                  Zuordnung(en) angewendet
                </span>
                {autoMatchToday.skipped > 0 && (
                  <span className="text-[11px] text-slate-500">
                    · {autoMatchToday.skipped} übersprungen
                  </span>
                )}
                {autoMatchToday.failed > 0 && (
                  <span className="text-[11px] text-rose-600 font-medium">
                    · {autoMatchToday.failed} Fehler
                  </span>
                )}
                <span className="text-[11px] text-slate-500">
                  · {autoMatchToday.runs} Lauf/Läufe
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/settings")}
              data-testid="dashboard-auto-match-details-button"
            >
              Details
            </Button>
          </div>
        </Card>
      )}

      {/* Vendor-Hygiene Card */}
      {vendorHygiene && (vendorHygiene.unassigned_count > 0 || vendorHygiene.low_score_count > 0 || vendorHygiene.new_suppliers_30d > 0) && (
        <Card className="p-5 border-amber-200 bg-amber-50/40" data-testid="vendor-hygiene-card">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                🔍 Lieferanten-Hygiene
              </h3>
              <p className="text-xs text-slate-500">
                Datenqualität deiner Belege — 5 Min. täglich „Aufräumen" hält das Journal sauber.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/reports?tab=vendor-hygiene")}
              data-testid="vendor-hygiene-report-link"
            >
              Vollständiger Report →
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => navigate("/receipts?supplier=missing")}
              className="text-left bg-white border border-amber-200 rounded-md p-3 hover:border-amber-400 hover:bg-amber-50/80 transition-colors"
              data-testid="vendor-hygiene-unassigned"
            >
              <div className="text-2xl font-semibold text-amber-700">
                {vendorHygiene.unassigned_count}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Belege ohne Lieferant
                <span className="text-slate-400 ml-1">
                  ({vendorHygiene.total_receipts > 0 ? Math.round(100 * vendorHygiene.unassigned_count / vendorHygiene.total_receipts) : 0}%)
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/receipts?supplier=lowscore")}
              className="text-left bg-white border border-amber-200 rounded-md p-3 hover:border-amber-400 hover:bg-amber-50/80 transition-colors"
              data-testid="vendor-hygiene-lowscore"
            >
              <div className="text-2xl font-semibold text-rose-600">
                {vendorHygiene.low_score_count}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Niedriger Match-Score
                <span className="text-slate-400 ml-1">(&lt;85%)</span>
              </div>
            </button>
            <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="vendor-hygiene-new-suppliers">
              <div className="text-2xl font-semibold text-emerald-700">
                {vendorHygiene.new_suppliers_30d}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Neue Lieferanten (30 Tage)
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Chart + Inbox */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2" data-testid="dashboard-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Einnahmen vs. Ausgaben
              </h3>
              <p className="text-xs text-slate-500">Letzte 6 Monate</p>
            </div>
          </div>
          <div className="h-64 w-full min-w-[200px]">
            <ResponsiveContainer width="100%" height="100%" minHeight={240} minWidth={200} initialDimension={{ width: 800, height: 240 }}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  formatter={(v) => formatCHF(v, stats?.currency)}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar dataKey="Einnahmen" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Ausgaben" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="dashboard-inbox-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <Inbox className="h-4 w-4" />
                Beleg-Inbox
              </h3>
              <p className="text-xs text-slate-500">
                {stats?.open_receipts_count || 0} offen · {formatCHF(stats?.open_receipts_amount || 0, stats?.currency)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/receipts")}
              data-testid="dashboard-inbox-all-button"
            >
              Alle <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {(!stats?.inbox_receipts || stats.inbox_receipts.length === 0) ? (
            <div className="py-8 text-center text-xs text-slate-400">
              Keine offenen Belege
            </div>
          ) : (
            <ul className="space-y-2" data-testid="dashboard-inbox-list">
              {stats.inbox_receipts.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate("/receipts")}
                  data-testid={`dashboard-inbox-item-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{r.label}</div>
                    {r.due_date && (
                      <div className="text-[11px] text-slate-500">{formatDate(r.due_date)}</div>
                    )}
                  </div>
                  <div className="text-slate-900 font-medium flex-shrink-0">
                    {formatCHF(r.amount, r.currency)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Open positions */}
      <div className="grid gap-4 lg:grid-cols-2">
        {stats?.dunning_due_count > 0 && (
          <Card
            className="lg:col-span-2 p-4 bg-gradient-to-r from-amber-50 to-rose-50 border-amber-200"
            data-testid="dashboard-dunning-card"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <Bell className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-amber-900">
                    {stats.dunning_due_count} Mahnung{stats.dunning_due_count === 1 ? "" : "en"} anstehend
                  </div>
                  <div className="text-xs text-amber-800">
                    Offener Betrag: {formatCHF(stats.dunning_due_amount, stats.currency)} · Ihre Kunden haben die Fälligkeit überschritten.
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="rounded-full bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
                onClick={() => navigate("/dunning")}
                data-testid="dashboard-dunning-cta"
              >
                Mahnwesen öffnen
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          </Card>
        )}

        <Card className="p-5" data-testid="dashboard-open-invoices-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <FileText className="h-4 w-4" />
                Offene Kundenrechnungen
              </h3>
              <p className="text-xs text-slate-500">
                {stats?.open_invoices_count || 0} offen · {formatCHF(stats?.open_invoices_amount || 0, stats?.currency)}
                {stats?.overdue_invoices_count ? (
                  <span className="ml-1.5 inline-flex items-center gap-1 text-rose-600">
                    <AlertTriangle className="h-3 w-3" />
                    {stats.overdue_invoices_count} überfällig
                  </span>
                ) : null}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/invoices")}
              data-testid="dashboard-invoices-all-button"
            >
              Alle <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {(!stats?.top_open_invoices || stats.top_open_invoices.length === 0) ? (
            <div className="py-8 text-center text-xs text-slate-400">
              Keine offenen Rechnungen
            </div>
          ) : (
            <ul className="space-y-2" data-testid="dashboard-invoices-list">
              {stats.top_open_invoices.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50"
                  data-testid={`dashboard-invoice-item-${inv.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{inv.label}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      {inv.due_date && <span>Fällig: {formatDate(inv.due_date)}</span>}
                      {inv.days_overdue > 0 && (
                        <Badge variant="destructive" className="h-4 text-[10px] px-1.5">
                          {inv.days_overdue} Tage
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-slate-900 font-medium flex-shrink-0">
                    {formatCHF(inv.amount, inv.currency)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5" data-testid="dashboard-open-bills-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <FileInput className="h-4 w-4" />
                Offene Eingangsrechnungen
              </h3>
              <p className="text-xs text-slate-500">
                {stats?.open_bills_count || 0} offen · {formatCHF(stats?.open_bills_amount || 0, stats?.currency)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/bills")}
              data-testid="dashboard-bills-all-button"
            >
              Alle <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {(!stats?.top_open_bills || stats.top_open_bills.length === 0) ? (
            <div className="py-8 text-center text-xs text-slate-400">
              Keine offenen Eingangsrechnungen
            </div>
          ) : (
            <ul className="space-y-2" data-testid="dashboard-bills-list">
              {stats.top_open_bills.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50"
                  data-testid={`dashboard-bill-item-${b.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{b.label}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      {b.due_date && <span>Fällig: {formatDate(b.due_date)}</span>}
                      {b.days_overdue > 0 && (
                        <Badge variant="destructive" className="h-4 text-[10px] px-1.5">
                          {b.days_overdue} Tage
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-slate-900 font-medium flex-shrink-0">
                    {formatCHF(b.amount, b.currency)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <AssetWarningsWidget companyId={company?._id || localStorage.getItem("demoCompanyId")} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CashflowForecastWidget companyId={company?._id || localStorage.getItem("demoCompanyId")} days={90} />
        <AnomaliesWidget companyId={company?._id || localStorage.getItem("demoCompanyId")} />
      </div>

      <div className="mt-6">
        <MultiAssetTimelineWidget companyId={company?._id || localStorage.getItem("demoCompanyId")} />
      </div>

      <div className="mt-6">
        <AutoBookedTodayCard companyId={company?._id || localStorage.getItem("demoCompanyId")} days={1} />
      </div>

      <div className="mt-6">
        <ActivityFeedWidget companyId={company?._id || localStorage.getItem("demoCompanyId")} limit={15} />
      </div>

      {loading && (
        <div
          className="fixed bottom-4 right-4 text-xs text-slate-600 bg-white border border-slate-200 px-3 py-2 rounded-full shadow-sm flex items-center gap-2"
          data-testid="dashboard-loading-indicator"
        >
          <RefreshCw className="h-3 w-3 animate-spin" />
          Lade Daten …
        </div>
      )}
    </div>
  );
}
